import { Alert, Box, Stack } from '@mui/material';
import { CheckCircle, Replay } from '@mui/icons-material';
import { ReactElement } from 'react';
import {
  DataTable,
  Identifier,
  Labeled,
  ListContextProvider,
  ResourceContextProvider,
  RichTextField,
  UpdateButton,
  useCanAccess,
  useGetList,
  useList,
} from 'react-admin';
import TzDateField from '../../../Components/TzDate/TzDateField';
import { type TrackedErrorIssueResource } from '../../../Entities/TrackedErrorIssue';

interface UnresolvedNetSuiteCustomerSyncErrorsProps {
  merchantId: Identifier;
}

const NETSUITE_CUSTOMER_SYNC_OPERATION = 'Sync Merchant With NetSuite Customer';
const NETSUITE_SYNC_CATEGORY = 'ExternalSync';

export default function UnresolvedNetSuiteCustomerSyncErrors({
  merchantId,
}: UnresolvedNetSuiteCustomerSyncErrorsProps): ReactElement {
  const trackedErrorWriteAccess = useCanAccess({
    action: 'write',
    resource: 'tracked_error_issues',
  });

  const trackedErrorsQuery = useGetList<TrackedErrorIssueResource>('tracked_error_issues', {
    filter: {
      equals: {
        category: NETSUITE_SYNC_CATEGORY,
        is_resolved: false,
        merchant: { id: merchantId },
        operation: NETSUITE_CUSTOMER_SYNC_OPERATION,
      },
    },
    sort: { field: 'attributes.last_occurred_at_utc', order: 'DESC' },
  });

  const trackedErrors = trackedErrorsQuery.data ?? [];
  const isPending = trackedErrorsQuery.isPending || trackedErrorsQuery.isLoading;

  const listContext = useList<TrackedErrorIssueResource>({
    data: trackedErrors,
    isPending,
    perPage: Math.max(trackedErrors.length, 1),
    sort: { field: 'attributes.last_occurred_at_utc', order: 'DESC' },
  });

  return (
    <Labeled label="Unresolved NetSuite Sync Errors" sx={{ mt: 2, width: '100%' }}>
      {trackedErrorsQuery.error ? (
        <Alert severity="error">Unable to load NetSuite customer sync errors.</Alert>
      ) : trackedErrors.length === 0 && !isPending ? (
        <Alert severity="info">No unresolved NetSuite customer sync errors found.</Alert>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <ListContextProvider value={listContext}>
            <ResourceContextProvider value="tracked_error_issues">
              <DataTable bulkActionButtons={false} rowClick="show">
                <DataTable.Col source="attributes.category" label="Category" />
                <DataTable.Col source="attributes.operation" label="Operation" />
                <DataTable.NumberCol source="attributes.occurrence_count" label="Occurrences" />
                <DataTable.Col source="attributes.last_occurred_at_utc" label="Last Occurred">
                  <TzDateField
                    source="attributes.last_occurred_at_utc"
                    showTime
                    serverTimezone="UTC"
                  />
                </DataTable.Col>
                <DataTable.Col source="attributes.last_message" label="Last Message">
                  <RichTextField source="attributes.last_message" />
                </DataTable.Col>
                {trackedErrorWriteAccess.canAccess && (
                  <DataTable.Col
                    label="Actions"
                    disableSort
                    render={(record: TrackedErrorIssueResource) => (
                      <Stack direction="row" spacing={1}>
                        <UpdateButton
                          label="Mark resolved"
                          color="success"
                          sx={{ color: 'text.primary' }}
                          icon={<CheckCircle />}
                          variant="contained"
                          disabled={record.attributes.is_resolved}
                          data={{ attributes: { is_resolved: true } }}
                        />
                        <UpdateButton
                          label="Mark unresolved"
                          color="warning"
                          icon={<Replay />}
                          variant="contained"
                          sx={{ color: 'text.primary' }}
                          disabled={!record.attributes.is_resolved}
                          data={{ attributes: { is_resolved: false } }}
                        />
                      </Stack>
                    )}
                  />
                )}
              </DataTable>
            </ResourceContextProvider>
          </ListContextProvider>
        </Box>
      )}
    </Labeled>
  );
}
